import { useMemo } from "react";

import { ProvisioningFunnelUfoProvider } from "@/components/signup/provisioning-funnel-ufo/provisioning-funnel-ufo";
import { ProductKey } from "@/components/signup/utils";
import { experiments, ExperimentView } from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { AnalyticsClientUpdater } from "../../components/analytics-client-updater";
import { AutoSelectThemePreference } from "../../components/auto-select-theme-preference";
import { useCloudStatus, useCloudStatusRedirection } from "../../hooks";
import {
    TenantAwareStatsigClientUpdaterProvider,
    useTenantAwareStatsigClientUpdater
} from "../../hooks/use-tenant-aware-statsig-client-updater";
import { PerceivedProvisioningStartTimer } from "../../utils/perceived-provisioning/perceived-provisioning-start-timer";
import { jiraScreenConfig } from "../../utils/screen-manager/jira-screen-config";
import { jiraScreenDictionary } from "../../utils/screen-manager/jira-screen-dictionary";
import { JiraAnimatedProvisioningScreen } from "../jira/jira-animated-provisioning-screen/jira-animated-provisioning-screen";
import { ProductRedirector } from "../product-redirector/product-redirector";

import { BaseProvisioningUI } from "./base-provisioning-ui";

/**
 * Component that shows provisioning UI for Jira.
 * @returns @type {JSX.Element}
 */
const ProvisioningContent = () => (
    <>
        <AutoSelectThemePreferenceWrapper />
        <AnalyticsClientUpdater />
        <BaseProvisioningUI
            screenConfig={jiraScreenConfig}
            screenDictionary={jiraScreenDictionary}
            screenFallback={
                <PerceivedProvisioningStartTimer>
                    <ProductRedirector>
                        <JiraAnimatedProvisioningScreen />
                    </ProductRedirector>
                </PerceivedProvisioningStartTimer>
            }
        />
    </>
);

export const JiraProvisioningUI = () => {
    const { addBlockingRedirectPromise } = useCloudStatusRedirection();
    const isProvisioningFunnelUfoEnabled = checkFeatureFlagGate(
        featureFlags.provisioningFunnelUfoImprovements
    );

    return (
        <TenantAwareStatsigClientUpdaterProvider>
            {isProvisioningFunnelUfoEnabled ? (
                <ProvisioningFunnelUfoProvider
                    initialCustomData={{ productKey: ProductKey.JIRA_SOFTWARE }}
                    addBlockingRedirectPromise={addBlockingRedirectPromise}
                >
                    <ProvisioningContent />
                </ProvisioningFunnelUfoProvider>
            ) : (
                <ProvisioningContent />
            )}
        </TenantAwareStatsigClientUpdaterProvider>
    );
};

const AutoSelectThemePreferenceWrapper = () => {
    const { updatedStatsigClientWithCloudId } =
        useTenantAwareStatsigClientUpdater();
    const { cloudId, createCloudStatus } = useCloudStatus();
    const { addBlockingRedirectPromise } = useCloudStatusRedirection();

    const experimentPreconditionsMet = useMemo(
        () =>
            cloudId &&
            updatedStatsigClientWithCloudId &&
            createCloudStatus === "success",
        [cloudId, createCloudStatus, updatedStatsigClientWithCloudId]
    );

    // experimentPreconditionsMet ensures that the site and statsig client is ready before we enroll and try to hit
    // the Jira theme preference API. Otherwise, it will throw an error if the tenant is not ready.
    return experimentPreconditionsMet ? (
        <ExperimentView
            experiment={experiments.jiraOnboardingAutodetectSystemTheme}
            render={(value) => {
                if (value) {
                    return (
                        <AutoSelectThemePreference
                            cloudId={cloudId}
                            product="jira"
                            onRequest={addBlockingRedirectPromise}
                        />
                    );
                }

                return null;
            }}
        />
    ) : null;
};
